import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { VehicleDeletedReason, vehicleDeletedReasonLabels } from "../../models/Vehicle";
import {
  validateVehicleDeletion,
  VEHICLE_DELETION_NOTE_MAX_LENGTH,
} from "../../utils/vehicleDeletion";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface DeleteVehicleModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  isLoading: boolean;
  onConfirm: (reason: VehicleDeletedReason, note?: string) => void;
}

interface FormFields {
  deletedReason?: VehicleDeletedReason;
  deletedNote: string;
}

// Deleting a vehicle now requires a reason. The Delete button stays disabled until the reason (and,
// for "Other", the note) is valid, so a destructive action is never accepted and then rejected by
// the API - the rules here mirror the server's.
export default function DeleteVehicleModal({
  open,
  setOpen,
  isLoading,
  onConfirm,
}: DeleteVehicleModalProps) {
  const { t } = useTranslation("vehicles");

  const { register, setValue, watch, reset } = useForm<FormFields>({
    // No default reason: pre-selecting one would let someone delete a vehicle without ever making
    // a choice, which is the whole point of the field.
    defaultValues: { deletedReason: undefined, deletedNote: "" },
  });

  // Clear on close so reopening doesn't inherit the previous attempt's reason.
  useEffect(() => {
    if (!open) reset({ deletedReason: undefined, deletedNote: "" });
  }, [open, reset]);

  const reason = watch("deletedReason");
  const note = watch("deletedNote");
  const validation = validateVehicleDeletion(reason, note);

  const reasonOptions = (Object.values(VehicleDeletedReason) as VehicleDeletedReason[])
    .filter((value): value is VehicleDeletedReason => typeof value === "number")
    .map((value) => ({ id: value, name: t(vehicleDeletedReasonLabels[value]) }));

  const noteRequired = reason === VehicleDeletedReason.Other;

  return (
    <ConfirmModal
      open={open}
      setOpen={setOpen}
      onConfirm={() => {
        if (!validation.isValid || reason === undefined) return;
        onConfirm(reason, validation.note);
      }}
      isLoading={isLoading}
      confirmDisabled={!validation.isValid}
      title={t("Delete this vehicle?")}
      message={t(
        "This will soft-delete the vehicle. It will not appear in normal vehicle results unless deleted filters are explicitly included."
      )}
      confirmButtonText={t("Delete Vehicle")}
    >
      <div className="mt-4 flex flex-col gap-y-3">
        <SelectInput
          label={t("Reason")}
          register={register}
          registerName="deletedReason"
          options={reasonOptions}
          setValue={setValue}
          watch={watch}
          cols="w-full"
          placeholder={t("Select a reason")}
          rounded
        />

        <TextInput
          label={noteRequired ? t("Note") : t("Note (optional)")}
          register={register}
          registerName="deletedNote"
          richText
          rows={3}
          cols="w-full"
          maxLength={VEHICLE_DELETION_NOTE_MAX_LENGTH}
          required={noteRequired}
        />

        {/* Says why Delete is disabled. Without it the button just looks broken. */}
        {!validation.isValid && validation.message ? (
          <p className="text-sm text-red-600">{t(validation.message)}</p>
        ) : null}
      </div>
    </ConfirmModal>
  );
}
